import { Controller } from '@hotwired/stimulus'

// CMP-302 — row repeater + live risk scoring for the TACCP/VACCP JSONB editors
// (threats / vulnerabilities). Each row is a set of `<field>[][...]` inputs with
// no index; adding clones the <template>, removing drops the row. Beyond add/
// remove, it reveals what the model already knows: as likelihood × impact are
// entered it shows the score, flags a row red when it crosses the threshold
// unmitigated (amber when mitigated), and keeps a running "N high-risk, unmitigated"
// count in the sticky footer — the document's headline. No index bookkeeping:
// DOM order is the only thing that matters (Rails groups each row into its hash).
export default class extends Controller {
  static targets = ['rows', 'template', 'threshold', 'summary', 'empty', 'addButton']
  static values = { openLabel: String, coveredLabel: String, summaryTemplate: String, thresholdDefault: Number }

  connect () {
    this.rescoreAll()
    this.renumber()
    this.refreshEmptyState()
  }

  add () {
    this.rowsTarget.appendChild(this.templateTarget.content.cloneNode(true))
    const row = this.rowsTarget.lastElementChild
    this.renumber()
    this.refreshEmptyState()
    row.querySelector('input[type="text"]')?.focus()
    row.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    this.scoreRow(row)
  }

  remove (event) {
    const row = event.target.closest('[data-role="row"]')
    if (!row) return
    row.remove()
    this.renumber()
    this.refreshEmptyState()
    this.updateSummary()
  }

  // A segmented-scale button was tapped: write its value to the hidden input,
  // move the active state, and rescore the row.
  pick (event) {
    const button = event.currentTarget
    const group = button.closest('[data-scale]')
    group.querySelector('input[type="hidden"]').value = button.dataset.value
    group.querySelectorAll('button').forEach((other) => {
      const active = other === button
      other.classList.toggle('btn-active', active)
      other.classList.toggle('btn-primary', active)
      other.setAttribute('aria-pressed', active)
    })
    this.scoreRow(button.closest('[data-role="row"]'))
  }

  // A qualitative field that can change the verdict (mitigation, commodity,
  // vendor, an auth-strategy checkbox) changed.
  rescore (event) {
    this.scoreRow(event.target.closest('[data-role="row"]'))
  }

  rescoreAll () {
    this.rowElements.forEach((row) => this.scoreRow(row))
  }

  scoreRow (row) {
    if (!row) return
    const scoreEl = row.querySelector('[data-role="score"]')
    const chip = row.querySelector('[data-role="risk"]')
    if (!scoreEl || !chip) return

    const likelihood = Number(row.querySelector('[data-scale="likelihood"] input[type="hidden"]')?.value)
    const impact = Number(row.querySelector('[data-scale="impact"] input[type="hidden"]')?.value)
    const scored = Boolean(likelihood && impact)
    scoreEl.textContent = scored ? String(likelihood * impact) : '—'

    // Mirror Vulnerability#high_risk? exactly: a watch-list commodity or a high-risk
    // vendor is high-risk on its own, REGARDLESS of score — so it must flag even
    // before likelihood × impact are entered. The live headline must never read
    // safer than what the server will save. (Server truth still governs on save —
    // this is honest live feedback, not authorization.)
    const commodityHigh = row.querySelector('[data-role="commodity"] option:checked')?.dataset.risk === 'high'
    const vendorHigh = row.querySelector('[data-role="vendor"] option:checked')?.dataset.risk === 'high'
    const highRisk = (scored && likelihood * impact >= this.threshold) || commodityHigh || vendorHigh

    if (!highRisk) this.applyRisk(row, chip, scored ? 'ok' : 'incomplete')
    else if (this.rowMitigated(row)) this.applyRisk(row, chip, 'high-covered', this.coveredLabelValue)
    else this.applyRisk(row, chip, 'high-open', this.openLabelValue)
    this.updateSummary()
  }

  // Set the row's risk state + its left-border accent (explicit classes so Tailwind
  // reliably emits them) and the chip.
  applyRisk (row, chip, state, label) {
    row.dataset.risk = state
    row.classList.remove('border-l-4', 'border-l-error', 'border-l-warning')
    if (state === 'high-open') {
      row.classList.add('border-l-4', 'border-l-error')
      chip.className = 'badge badge-sm badge-error'
      chip.textContent = label
    } else if (state === 'high-covered') {
      row.classList.add('border-l-4', 'border-l-warning')
      chip.className = 'badge badge-sm badge-warning'
      chip.textContent = label
    } else {
      chip.className = 'hidden'
      chip.textContent = ''
    }
  }

  // Threat: a mitigation program is selected. Fraud: at least one authentication
  // strategy is checked.
  rowMitigated (row) {
    const program = row.querySelector('[data-role="mitigation"]')
    if (program) return program.value !== ''
    const strategies = row.querySelectorAll('[data-role="authentication"] input[type="checkbox"]')
    return Array.from(strategies).some((box) => box.checked)
  }

  updateSummary () {
    if (!this.hasSummaryTarget) return
    const open = this.rowElements.filter((row) => row.dataset.risk === 'high-open').length
    this.summaryTarget.dataset.state = open === 0 ? 'ok' : 'alert'
    this.summaryTarget.textContent = this.summaryTemplateValue.replace('%{count}', String(open))
  }

  renumber () {
    this.rowElements.forEach((row, index) => {
      const label = row.querySelector('[data-role="index"]')
      if (label) label.textContent = String(index + 1)
    })
  }

  // Zero rows → show the empty state and hide the persistent add button (so the
  // empty state's own CTA is the single add affordance); otherwise the reverse.
  refreshEmptyState () {
    const hasRows = this.rowElements.length !== 0
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle('hidden', hasRows)
    if (this.hasAddButtonTarget) this.addButtonTarget.classList.toggle('hidden', !hasRows)
  }

  get threshold () {
    if (this.hasThresholdTarget) {
      const value = Number(this.thresholdTarget.value)
      if (value) return value
    }
    return this.thresholdDefaultValue || 12
  }

  get rowElements () {
    return Array.from(this.rowsTarget.querySelectorAll('[data-role="row"]'))
  }
}
